import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

export type IdentityProviderKind = "EntraId" | "Oidc" | "IdentityServer";
export const IDENTITY_PROVIDER_KINDS: IdentityProviderKind[] = ["EntraId", "Oidc", "IdentityServer"];

/** A claim value (group object id, app role…) that grants a role; a missing claim means the provider's groups claim. */
export interface IdpRoleMapping { claim?: string | null; value: string; label?: string | null; roleKey: string }
export interface IdpWorkspaceMapping { claim?: string | null; value: string; label?: string | null; workspaceKey: string; isOwner: boolean }

export interface IdentityProviderInput {
  name: string;
  nameAr?: string | null;
  kind: IdentityProviderKind;
  issuer: string;
  authorizationEndpoint?: string | null;
  tokenEndpoint?: string | null;
  userInfoEndpoint?: string | null;
  jwksUri?: string | null;
  endSessionEndpoint?: string | null;
  clientId: string;
  clientSecretRef?: string | null;
  scopes: string;
  usePkce: boolean;
  subjectClaim: string;
  emailClaim?: string | null;
  nameClaim?: string | null;
  nameArClaim?: string | null;
  groupsClaim?: string | null;
  roleMappings: IdpRoleMapping[];
  workspaceMappings: IdpWorkspaceMapping[];
  defaultRoleKeys: string[];
  justInTimeProvisioning: boolean;
  syncOnSignIn: boolean;
  linkByEmail: boolean;
  allowedEmailDomains: string[];
  extraAuthorizeParams: Record<string, string>;
  iconUrl?: string | null;
  enabled: boolean;
  simulated: boolean;
  /** The directory's MFA stands in for the platform's: every sign-in must show one completed within this many minutes. Null: only where the host requires MFA. */
  mfaWithinMinutes?: number | null;
  /** Microsoft Entra ID only: names, title, department, mobile, manager and photo from Microsoft Graph at every sign-in. */
  profileSync: boolean;
  /** The on-premises extension attribute (extensionAttribute1–15) holding the Arabic name, read with the profile. */
  arabicNameAttribute?: string | null;
  /** The applications whose sign-in pages offer this provider: "econic", "ncp"; empty = both. */
  apps: string[];
}

export interface IdentityProvider extends IdentityProviderInput {
  id: string;
  key: string;
  audience: string;
  lastTestedAt?: string | null;
  lastTestResult?: string | null;
  linkedUsers: number;
  lastSignInAt?: string | null;
  updatedAt: string;
}

export interface IdentityProviderTestResult { ok: boolean; message: string; testedAt: string }

export interface IdentityProviderSignIn {
  at: string;
  outcome: "signedIn" | "refused" | "failed";
  name?: string | null;
  email?: string | null;
  reason?: string | null;
  message?: string | null;
  roles: string[];
  workspaces: string[];
  matched: string[];
  created: boolean;
  linked: boolean;
}

const IDP = ["platform", "identity-providers"] as const;
const path = (key: string) => `/api/platform/identity-providers/${encodeURIComponent(key)}`;

export function useIdentityProviders() {
  const api = useApi();
  return useQuery({ queryKey: [...IDP, "list"], queryFn: () => api.get<IdentityProvider[]>("/api/platform/identity-providers"), staleTime: 10_000 });
}

export function useIdentityProviderSignIns(key: string | null) {
  const api = useApi();
  return useQuery({ queryKey: [...IDP, "sign-ins", key], queryFn: () => api.get<IdentityProviderSignIn[]>(`${path(key!)}/sign-ins?take=50`), enabled: !!key, refetchInterval: 30_000 });
}

export function useIdentityProviderActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: IDP });
    void qc.invalidateQueries({ queryKey: ["platform", "secrets"] });
  };
  return {
    create: useSave({ mutationFn: ({ key, ...input }: { key: string } & IdentityProviderInput) => api.post<IdentityProvider>(path(key), input), onSuccess: invalidate }),
    update: useSave({ mutationFn: ({ key, ...input }: { key: string } & IdentityProviderInput) => api.put<IdentityProvider>(path(key), input), onSuccess: invalidate }),
    setEnabled: useSave({ mutationFn: (input: { key: string; enabled: boolean }) => api.post<void>(`${path(input.key)}/enabled`, { enabled: input.enabled }), onSuccess: invalidate }),
    test: useSave({ mutationFn: (key: string) => api.post<IdentityProviderTestResult>(`${path(key)}/test`, {}), failed: t("admin:common.error"), onSettled: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(path(key)), onSuccess: invalidate }),
  };
}
